import { useCallback, useEffect, useRef, useState } from 'react';

import { HYMNS, HYMNS_BY_ID, hymnSeconds } from '../data/hymns.js';
import { Hymnal } from '../lib/hymnal.js';

/**
 * The hymn player.
 *
 * Much smaller than the narration player it replaces, and the reason is
 * structural rather than incidental: there is no file to load, so there is no
 * loading, no failure to load, and no fallback chain for when loading fails.
 * The music is generated from note data, so it either plays or the browser has
 * no Web Audio at all.
 *
 * Nothing starts until a tap. Every browser blocks audio before a gesture, and
 * an autoplay attempt on load is a silent failure that looks like a bug.
 */
export function useHymnal({ level = 0.7 } = {}) {
  const [hymnId, setHymnId] = useState(HYMNS[0].id);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [noteIndex, setNoteIndex] = useState(-1);
  const [error, setError] = useState(null);

  const organRef = useRef(null);
  const frameRef = useRef(0);

  if (!organRef.current && typeof window !== 'undefined') {
    organRef.current = new Hymnal();
  }

  const organ = organRef.current;
  const supported = !!organ?.supported;

  // Follow the audio clock rather than a timer of our own — it is the thing
  // the notes were actually scheduled against.
  useEffect(() => {
    if (!playing) {
      cancelAnimationFrame(frameRef.current);
      return undefined;
    }
    const step = () => {
      setPosition(organ.position());
      setNoteIndex(organ.playingIndex());
      frameRef.current = requestAnimationFrame(step);
    };
    frameRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frameRef.current);
  }, [playing, organ]);

  useEffect(() => {
    organ?.setLevel(level);
  }, [organ, level]);

  // Leaving the page should not leave an organ playing into an empty room.
  useEffect(() => () => organ?.stop(), [organ]);

  const play = useCallback(
    async (id) => {
      if (!organ) return;
      setError(null);
      const started = await organ.play(id ?? hymnId);
      if (!started) {
        setError('This browser will not play audio.');
        return;
      }
      if (id) setHymnId(id);
      setPlaying(true);
    },
    [organ, hymnId],
  );

  const pause = useCallback(() => {
    organ?.pause();
    setPlaying(false);
  }, [organ]);

  const toggle = useCallback(() => (playing ? pause() : play()), [playing, pause, play]);

  const select = useCallback(
    (id, { autoplay = true } = {}) => {
      setHymnId(id);
      setPosition(0);
      setNoteIndex(-1);
      if (autoplay || playing) play(id);
      else organ?.stop();
    },
    [play, playing, organ],
  );

  const step = useCallback(
    (delta) => {
      const at = HYMNS.findIndex((h) => h.id === hymnId);
      // Wraps both ways, so Next at the end of the hymnal starts it again.
      const next = HYMNS[(at + delta + HYMNS.length) % HYMNS.length];
      select(next.id, { autoplay: playing });
    },
    [hymnId, playing, select],
  );

  const hymn = HYMNS_BY_ID[hymnId];

  return {
    hymns: HYMNS,
    hymn,
    hymnId,
    playing,
    position,
    noteIndex,
    supported,
    error,
    seconds: hymnSeconds(hymn),
    play,
    pause,
    toggle,
    select,
    next: () => step(1),
    previous: () => step(-1),
  };
}
